/**
 * Gallery Page — Browse all Private + Shared media.
 * Single click → preview → Create Post.
 * Multi-select → Create Thread from selected.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Video, Upload, MapPin, Calendar, CheckCircle, ArrowUpDown, Loader2, Check, CheckSquare, Square, List } from 'lucide-react'
import { useMediaGallery, MediaItem } from '../hooks/useMediaGallery'
import { useMediaPreview } from '../contexts/MediaPreviewContext'

const PREVIEW_COUNT = 6

export default function Gallery() {
  const navigate = useNavigate()
  const { openPreview } = useMediaPreview()
  const [activeTab, setActiveTab] = useState<'private' | 'shared'>('private')
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const gallery = useMediaGallery()

  useEffect(() => {
    gallery.loadMedia(activeTab)
    setSelected(new Set())
  }, [activeTab])

  const handleItemClick = (item: MediaItem) => {
    if (selectMode) {
      // Toggle selection
      setSelected(prev => {
        const next = new Set(prev)
        next.has(item.id) ? next.delete(item.id) : next.add(item.id)
        return next
      })
    } else {
      // Single click → preview with create post option
      openPreview({
        mediaUrl: item.mediaUrl,
        mediaType: item.mediaType === 'video' ? 'video' : 'image',
        fromGallery: true,
      })
    }
  }

  const handleCreatePost = () => {
    if (selected.size !== 1) return
    const item = gallery.filtered.find(m => selected.has(m.id))
    if (item) navigate('/create', { state: { mediaUrl: item.mediaUrl, mediaType: item.mediaType } })
  }

  const handleCreateThread = () => {
    const items = gallery.filtered
      .filter(m => selected.has(m.id))
      .map(m => ({
        url: m.mediaUrl,
        thumbnail: m.thumbnailUrl,
        type: m.mediaType === 'video' ? 'video' : 'image',
      }))
    navigate('/create-thread', { state: { preSelectedMedia: items } })
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Gallery</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pb-3">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5 w-fit">
            {(['private', 'shared'] as const).map(tab => (
              <button key={tab} onClick={() => { setActiveTab(tab); gallery.setExpandedGroups(new Set()); setSelected(new Set()) }}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeTab === tab ? 'bg-onrecord-orange text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}>
                {tab === 'private' ? 'Private' : 'Shared'}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
            {(['all', 'photo', 'video'] as const).map(t => (
              <button key={t} onClick={() => gallery.setFilter(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  gallery.filter === t ? 'bg-onrecord-orange text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}>
                {t === 'all' ? 'All' : t === 'photo' ? 'Photos' : 'Videos'}
              </button>
            ))}
          </div>
          <button onClick={() => gallery.setSort(s => s === 'newest' ? 'oldest' : 'newest')}
            className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200">
            <ArrowUpDown className="w-3 h-3" />{gallery.sort === 'newest' ? 'Newest' : 'Oldest'}
          </button>
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
            {([
              { key: 'date-location', label: 'Date+Place' },
              { key: 'location', label: 'Place' },
              { key: 'date', label: 'Date' },
              { key: 'none', label: 'Flat' },
            ] as const).map(({ key, label }) => (
              <button key={key} onClick={() => { gallery.setGroupBy(key); gallery.setExpandedGroups(new Set()) }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  gallery.groupBy === key ? 'bg-gray-700 text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}>{label}</button>
            ))}
          </div>
          {gallery.locations.length > 1 && (
            <select value={gallery.locationFilter} onChange={e => gallery.setLocationFilter(e.target.value)}
              className="px-3 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100 border-0 outline-none cursor-pointer">
              <option value="">All locations</option>
              {gallery.locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          )}
          <span className="text-xs text-gray-400">{gallery.filtered.length} items</span>
          <button
            onClick={() => { setSelectMode(!selectMode); setSelected(new Set()) }}
            className={`ml-auto flex items-center gap-1.5 px-4 py-1 rounded-full text-xs font-medium transition-colors ${
              selectMode ? 'bg-onrecord-orange text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {selectMode ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {selectMode ? 'Cancel' : 'Select'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {gallery.loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-onrecord-orange animate-spin" />
          </div>
        ) : gallery.filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Upload className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-1">{activeTab === 'private' ? 'No private media' : 'No shared media'}</p>
            <p className="text-sm">{activeTab === 'private' ? 'Capture from the on record mobile app' : 'Media shared with you will appear here'}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(gallery.groups).map(([key, group]) => {
              const isExpanded = gallery.expandedGroups.has(key)
              const visible = isExpanded ? group.items : group.items.slice(0, PREVIEW_COUNT)
              const hasMore = group.items.length > PREVIEW_COUNT

              return (
                <div key={key}>
                  {gallery.groupBy !== 'none' && (
                    <div className="flex items-center justify-between mb-2 px-1">
                      <div className="flex items-center gap-2">
                        {(gallery.groupBy === 'date' || gallery.groupBy === 'date-location') && (
                          <><Calendar className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm font-semibold text-gray-700">{group.date}</span></>
                        )}
                        {gallery.groupBy === 'date-location' && <span className="text-gray-300">·</span>}
                        {(gallery.groupBy === 'location' || gallery.groupBy === 'date-location') && (
                          <><MapPin className="w-3.5 h-3.5 text-gray-400" /><span className="text-sm text-gray-500">{group.location}</span></>
                        )}
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{group.items.length}</span>
                      </div>
                      {hasMore && (
                        <button onClick={() => gallery.setExpandedGroups(prev => {
                          const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next
                        })} className="text-xs text-onrecord-orange hover:underline font-medium">
                          {isExpanded ? 'Show less' : `Show all ${group.items.length}`}
                        </button>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {visible.map(item => (
                      <div key={item.id} onClick={() => handleItemClick(item)}
                        className={`relative aspect-square cursor-pointer group rounded-lg overflow-hidden border-2 transition-all ${
                          selected.has(item.id) ? 'border-onrecord-orange ring-2 ring-onrecord-orange/30' : 'border-transparent hover:border-gray-300'
                        }`}>
                        <img src={item.thumbnailUrl || item.mediaUrl} alt="" className="w-full h-full object-cover" />
                        {item.mediaType === 'video' && (
                          <div className="absolute top-1.5 right-1.5 bg-black/60 rounded-full p-1"><Video className="w-3.5 h-3.5 text-white" /></div>
                        )}
                        {selectMode && (
                          <div className={`absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center ${
                            selected.has(item.id) ? 'bg-onrecord-orange' : 'bg-black/30 border border-white/50'
                          }`}>
                            {selected.has(item.id) && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                          {item.onRecordCode && (
                            <div className="flex items-center gap-1 text-white text-[10px]">
                              <CheckCircle className="w-2.5 h-2.5" />
                              <span className="font-mono">{item.onRecordCode}</span>
                            </div>
                          )}
                          {item.location?.city && (
                            <div className="text-white text-[10px]">
                              {item.location.city}{item.location.state ? `, ${item.location.state}` : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {!isExpanded && hasMore && (
                      <button onClick={() => gallery.setExpandedGroups(prev => new Set(prev).add(key))}
                        className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-onrecord-orange hover:text-onrecord-orange transition-colors">
                        <span className="text-lg font-bold">+{group.items.length - PREVIEW_COUNT}</span>
                        <span className="text-[10px]">more</span>
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Bottom action bar — shows when items selected */}
      {selected.size > 0 && (
        <div className="fixed bottom-0 left-64 right-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-6 py-3 flex items-center justify-between z-20 shadow-lg">
          <span className="text-sm font-medium text-gray-700">{selected.size} selected</span>
          <div className="flex items-center gap-3">
            <button onClick={handleCreatePost}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                selected.size === 1
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              disabled={selected.size !== 1}>
              Create Post
            </button>
            <button onClick={handleCreateThread}
              className="flex items-center gap-2 px-5 py-2 bg-onrecord-orange text-white rounded-full text-sm font-semibold hover:bg-onrecord-orange-dark transition-colors">
              <List className="w-4 h-4" />
              Create Thread ({selected.size})
            </button>
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
